import { Button, IconButton, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import MenuItem from '@mui/material/MenuItem';
import classNames from 'classnames';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useCallback } from 'react';
import { NumericInput } from '../../Input/NumericInput';
import { Select } from '../../Select/Select';
import { CalculatorOptions } from '../CompoundPage.hooks';
import { INITIAL_VALUES, investFrequencyMap, schema } from './CompoundForm.hooks';
import styles from './CompoundForm.module.css';

export type CompoundFormProps = {
  onSubmit: (options: CalculatorOptions) => void;
  className?: string;
};

const HelpTooltip = () => (
  <Tooltip
    title={
      <div style={{ fontSize: '1.1em', lineHeight: 1.5 }}>
        <p>
          장기 투자의 연평균 수익률을 입력하세요. 어떤 값을 사용해야 할지 잘 모르겠다면 아래를 참고하세요.
        </p>
        <ul style={{ paddingLeft: '20px' }}>
          <li>
            <strong>안정적인 채권형 펀드:</strong> 3~5%
          </li>
          <li>
            <strong>균형 잡힌 주식/채권 혼합 펀드:</strong> 5~7%
          </li>
          <li>
            <strong>성장 중심의 주식형 펀드:</strong> 7~10% 이상
          </li>
        </ul>
        <p>
          국민연금 기금운용본부의 장기 수익률 현황도 좋은 참고 자료가 될 수 있습니다.{' '}
          <a
            href="https://fund.nps.or.kr/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'white', textDecoration: 'underline' }}
          >
            (바로가기)
          </a>
        </p>
      </div>
    }
    arrow
    placement="top"
  >
    <IconButton size="small">
      <InfoIcon fontSize="small" />
    </IconButton>
  </Tooltip>
);

export const CompoundForm = ({ onSubmit, className }: CompoundFormProps) => {
  const handleSubmit = useCallback(
    (options: CalculatorOptions) => {
      onSubmit(options);
    },
    [onSubmit]
  );

  return (
    <Formik initialValues={INITIAL_VALUES} validationSchema={schema} onSubmit={handleSubmit}>
      {({ errors, setFieldValue }) => (
        <Form className={classNames(styles.root, className)}>
          <div className={styles.body}>
            <Field name="initialAmount">
              {({ field }: FieldProps) => (
                <NumericInput
                  label="초기 투자금 (원)"
                  placeholder="초기 투자금을 입력하세요"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(values) => {
                    setFieldValue(field.name, values.floatValue);
                  }}
                />
              )}
            </Field>
            {errors.initialAmount && <div className={styles.error}>{errors.initialAmount}</div>}
            <Field name="frequentInvestment">
              {({ field }: FieldProps) => (
                <NumericInput
                  label="월 투자금 (원)"
                  placeholder="매월 투자할 금액을 입력하세요"
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(values) => {
                    setFieldValue(field.name, values.floatValue);
                  }}
                />
              )}
            </Field>
            {errors.frequentInvestment && (
              <div className={styles.error}>{errors.frequentInvestment}</div>
            )}
            <Field name="investFrequency">
              {({ field }: FieldProps) => (
                <Select label="투자 주기" {...field}>
                  <MenuItem value={1}>{investFrequencyMap[1]}</MenuItem>
                  <MenuItem value={4}>{investFrequencyMap[4]}</MenuItem>
                  <MenuItem value={12}>{investFrequencyMap[12]}</MenuItem>
                </Select>
              )}
            </Field>
            {errors.investFrequency && <div className={styles.error}>{errors.investFrequency}</div>}
            <Field name="period">
              {({ field }: FieldProps) => (
                <NumericInput
                  label="투자기간 (년)"
                  placeholder="투자기간(년)을 입력하세요"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(values) => {
                    setFieldValue(field.name, values.floatValue);
                  }}
                />
              )}
            </Field>
            {errors.period && <div className={styles.error}>{errors.period}</div>}
            <Field name="interestPercentage">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      연 수익률(%)
                      <HelpTooltip />
                    </div>
                  }
                  placeholder="연간 투자 수익률을 입력하세요"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(values) => {
                    setFieldValue(field.name, values.floatValue);
                  }}
                />
              )}
            </Field>
            {errors.interestPercentage && (
              <div className={styles.error}>{errors.interestPercentage}</div>
            )}
          </div>
          <div className={styles.action}>
            <Button className={styles.button} variant="contained" type="submit">
              계산하기
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};
